import { useEffect, useRef, useState } from 'react';

import { appBorders, appColors, BackrsChip, BackrsIcon } from '@backrs_ui/common-ui';

import lubavLoader from '@backrs_ui/common-ui/src/assets/animations/dots-dotting.json';
import { axiosInstance as axios } from '@backrs_ui/grow-ui/src/core/axios';
import { solutionSessionIconMap } from '@backrs_ui/grow-ui/src/models/models';
import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  getSessionState,
  getActiveLubavSession,
  createSessionAction,
  typeGuardForSolutionSessionId,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';
import { setCustomPrompt } from '@backrs_ui/grow-ui/src/redux/solutionSession/solutionSession';
import { selectUserId, selectDoesUserHaveRole } from '@backrs_ui/grow-ui/src/redux/user/user';

import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  IconButton,
  Button,
  TextField,
  SxProps,
  Theme,
  Popover,
} from '@mui/material';
import Lottie from 'lottie-react';

import { OutputBox } from './OutputBox';
import { sessionTitles, boxTitles, sessionNames } from './SessionStrings';

type SolutionBoxProps = Readonly<{
  collapsedChips?: React.ReactNode | null;
}>;

export const SolutionBox = ({ collapsedChips = null }: SolutionBoxProps) => {
  const dispatch = useAppDispatch();

  const activeSession = useAppSelector(getActiveLubavSession);

  const { sessionId } = useAppSelector((state) => getSessionState(state, activeSession).coachLubav);
  const { chatMode } = useAppSelector(
    (state) => getSessionState(state, activeSession).solutionSession,
  );
  const { boxData, loading: isLoadingResources } = useAppSelector(
    (state) => getSessionState(state, activeSession).boxData,
  );

  const betaMode = useAppSelector(selectDoesUserHaveRole('Lubav Beta'));
  const userId = useAppSelector(selectUserId);

  const [selectedTab, setSelectedTab] = useState('main_content');
  const [showArrow, setShowArrow] = useState(true);
  const scrollRef = useRef(null);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [editorContent, setEditorContent] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    const fetchSystemPrompt = async () => {
      try {
        const response = await axios.post('/api/solution-session/get-system-prompt', activeSession);

        setEditorContent(response.data);
      } catch (error) {
        console.error('Error fetching system prompt:', error);
      }
    };

    fetchSystemPrompt();
  }, [activeSession]);

  useEffect(() => {
    const checkScroll = () => {
      if (scrollRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
        const isScrolledToEnd = scrollTop + clientHeight >= scrollHeight - 5;

        setShowArrow(!isScrolledToEnd);
      }
    };

    const scrollContainer = scrollRef.current;

    if (scrollContainer) {
      (scrollContainer as HTMLElement).addEventListener('scroll', checkScroll);
      checkScroll();
    }

    return () => {
      if (scrollContainer) {
        (scrollContainer as HTMLElement).removeEventListener('scroll', checkScroll);
      }
    };
  }, [boxData]);

  if (!activeSession || !typeGuardForSolutionSessionId(activeSession)) {
    return null;
  }

  const tabs = [
    { label: sessionTitles[activeSession], value: 'main_content' },
    { label: 'Connections', value: 'contacts' },
    { label: 'Resources', value: 'resources' },
  ];

  const renderTitleSection = () => (
    <Box sx={titleSectionStyle}>
      <Box sx={titleChipsContainerStyle}>
        <BackrsChip
          label={sessionTitles[activeSession]}
          variant={{
            type: 'LARGE_TEXT',
            backgroundColor: 'BLUE',
          }}
        />
        {collapsedChips}
        <style>{spinKeyframes}</style>
      </Box>
      {!isLoadingResources && (
        <Box sx={titleControlsStyle}>
          {betaMode && (
            <IconButton onClick={(event) => setAnchorEl(event.currentTarget)}>
              <BackrsIcon name="IconEdit" alt="Edit" style={iconStyle} />
            </IconButton>
          )}
          <Popover
            open={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
          >
            <Box sx={customPromptPopoverStyle}>
              <TextField
                autoFocus
                margin="dense"
                label="Custom Prompt"
                type="text"
                fullWidth
                multiline={true}
                rows={10}
                maxRows={10}
                value={editorContent}
                onChange={(e) => setEditorContent(e.target.value)}
                InputProps={{
                  style: textFieldInputStyle,
                }}
              />
              <Box sx={customPromptButtonContainerStyle}>
                <Button onClick={() => setAnchorEl(null)} sx={customPromptButtonStyle}>
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    dispatch(createSessionAction(setCustomPrompt, activeSession)(editorContent));
                    setAnchorEl(null);
                  }}
                  sx={customPromptButtonStyle}
                >
                  Save
                </Button>
              </Box>
            </Box>
          </Popover>
          <IconButton onClick={handleDownload} disabled={isDownloading} sx={iconButtonStyle}>
            {isDownloading ? (
              <Box sx={loaderBoxStyle}>
                <Lottie animationData={lubavLoader} loop style={animationStyle} />
              </Box>
            ) : (
              <BackrsIcon name="IconDownload" alt="Download" style={iconStyle} />
            )}
          </IconButton>

          {betaMode && (
            <IconButton>
              <BackrsIcon name="IconSend" alt="Send" style={iconStyleSmaller} />
            </IconButton>
          )}

          {betaMode && (
            <IconButton>
              <BackrsIcon name="IconBookmark" alt="Bookmark" style={iconStyleSmaller} />
            </IconButton>
          )}
        </Box>
      )}
    </Box>
  );

  const renderLoading = () => {
    return (
      <Box sx={loadingContainerStyle}>
        {renderTitleSection()}
        <Box sx={loadingBoxStyle}>
          <Typography sx={loadingTitleStyle}>
            {`Generating your ${sessionTitles?.[activeSession]?.toLowerCase() || 'session output'}`}
          </Typography>

          <Box sx={loadingIconContainerStyle}>
            <BackrsIcon
              name={solutionSessionIconMap[activeSession]}
              alt="Solution Session Icon"
              style={loadingImageStyle}
            />
          </Box>

          <Box sx={loadingBackgroundStyle}></Box>
          <Box sx={loadingSpinnerStyle}></Box>
        </Box>
      </Box>
    );
  };

  const renderContent = () => {
    return (
      <Box sx={contentContainerStyle}>
        {renderTitleSection()}
        <Box sx={mainContentStyle}>
          <Box sx={textContainerStyle}>
            <Tabs
              value={selectedTab}
              onChange={handleChange}
              aria-label="info tabs"
              TabIndicatorProps={{ style: { display: 'none' } }}
              sx={tabStyles}
            >
              {tabs.map((tab) => (
                <Tab key={tab.value} label={tab.label} value={tab.value} />
              ))}
            </Tabs>
            <Box ref={scrollRef} sx={scrollBox}>
              <OutputBox
                responseData={boxData.mainResponse}
                tab={selectedTab}
                solutionSession={activeSession}
              />
            </Box>
            {showArrow && (
              <Box sx={arrowContainerStyle}>
                <ArrowDownwardIcon fontSize="small" />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  const handleChange = (_: React.SyntheticEvent, newValue: string) => {
    setSelectedTab(newValue);
  };

  async function handleDownload() {
    if (!activeSession || !typeGuardForSolutionSessionId(activeSession)) {
      return null;
    }

    setIsDownloading(true);
    try {
      const PDFResponse = await axios.post(
        '/api/solution-session/create-pdf',
        {
          user_id: userId,
          session_id: sessionId,
          data: boxData,
          info_box_titles: boxTitles[activeSession],
          solution_session: activeSession,
        },
        {
          responseType: 'blob',
        },
      );
      const blob = new Blob([PDFResponse.data], { type: 'application/pdf' });

      const link = document.createElement('a');

      link.href = window.URL.createObjectURL(blob);

      link.download = `${sessionNames[activeSession].replace(/\s/g, '_')}_${new Date()
        .toISOString()
        .slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();

      // clean up
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setIsDownloading(false);
    }
  }

  if (boxData.mainResponse.main_response && chatMode && !isLoadingResources) {
    return renderContent();
  }

  if (isLoadingResources) {
    return renderLoading();
  }

  return <div></div>;
};

const contentContainerStyle: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  position: 'relative',
  height: '100%',
  maxHeight: '100%',
  overflow: 'hidden',
  flex: 1,
  flexGrow: 1,
};

const titleSectionStyle: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
};

const titleControlsStyle: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
};

const mainContentStyle = {
  backgroundColor: '#FFFFFF',
  overflow: 'auto',
  borderRadius: '12px',
  flex: 1,
  display: 'flex',
  marginBottom: '30px',
  flexDirection: 'column',
  ...appBorders.backrsBorders.black,
};

const loadingContainerStyle: SxProps<Theme> = {
  flex: 1,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  alignItems: 'flex-start',
  padding: '0px',
};

const loadingBoxStyle: SxProps<Theme> = {
  backgroundColor: '#FFFFFF',
  color: appColors.color.blue1,
  borderRadius: '8px',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  width: '100%',
  position: 'relative',
  zIndex: 2,
  overflow: 'hidden',
  boxSizing: 'border-box',
  ...appBorders.backrsBorders.black,
};

const loadingTitleStyle: SxProps<Theme> = {
  fontSize: 'clamp(16px, 2.5vw, 24pt)',
  fontWeight: 'bold',
  textAlign: 'left',
  position: 'absolute',
  top: '20px',
  left: '40px',
  zIndex: 3,
};

const loadingIconContainerStyle: SxProps<Theme> = {
  zIndex: 3,
};

const loadingImageStyle: React.CSSProperties = {
  width: '12vw',
  height: '12vw',
};

const loadingBackgroundStyle: SxProps<Theme> = {
  position: 'absolute',
  top: '4px',
  left: '4px',
  right: '4px',
  bottom: '4px',
  backgroundColor: '#FFFFFF',
  borderRadius: '4px',
  zIndex: 1,
};

const loadingSpinnerStyle: SxProps<Theme> = {
  position: 'absolute',
  zIndex: 0,
  backgroundColor: appColors.color.blue1,
  animation: 'spin 2s linear infinite',
  height: '250vh',
  width: '20vw',
};

const scrollBox: SxProps<Theme> = {
  overflowY: 'scroll',
  flex: 1,
  position: 'relative',
  backgroundColor: '#ffffff',
  display: 'flex',
  flexDirection: 'column',
  padding: '20px 20px',
};

const textContainerStyle: SxProps<Theme> = {
  borderRadius: '8px',
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  overflowY: 'auto',
  position: 'relative',
};

const tabStyles: SxProps<Theme> = {
  minHeight: '25px',
  '.MuiTabs-flexContainer': {
    display: 'flex',
  },
  '.MuiTab-root': {
    minHeight: '35px',
    maxWidth: '50%',
    padding: '6px 6px',
    fontSize: '11pt',
    lineHeight: '1',
    borderRadius: '0 0 0 0',
    textTransform: 'none',
    fontWeight: 'bold',
    color: appColors.color.blue1,
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    backgroundColor: 'rgba(222, 234, 255, 0.5)',
    alignItems: 'center',
    '&.Mui-selected': {
      color: appColors.color.blue1,
      backgroundColor: '#FFFFFF',
      borderRadius: '8px 8px 0 0',
    },
    '&:nth-of-type(2)': {
      backgroundColor: 'rgba(222, 234, 255, 0.75)',
      '&.Mui-selected': {
        color: appColors.color.blue1,
        backgroundColor: '#FFFFFF',
        borderRadius: '8px 8px 0 0',
      },
    },
  },
};

const arrowContainerStyle: SxProps<Theme> = {
  position: 'absolute',
  bottom: '15px',
  right: '50%',
  transform: 'translateX(50%)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: '20px',
  height: '20px',
  borderRadius: '50%',
  backgroundColor: '#808080',
  color: 'white',
  fontSize: '18px',
  opacity: 0.9,
};

const spinKeyframes = `
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const iconStyle: React.CSSProperties = {
  width: '20px',
  height: '20px',
};

const iconStyleSmaller: React.CSSProperties = {
  width: '17px',
  height: '17px',
};

const customPromptPopoverStyle: SxProps<Theme> = {
  padding: '20px',
  display: 'flex',
  flexDirection: 'column',
  width: '700px',
};

const titleChipsContainerStyle: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
};

const textFieldInputStyle: React.CSSProperties = {
  overflow: 'auto',
  fontSize: '10.5pt',
};

const customPromptButtonContainerStyle: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: '10px',
};

const customPromptButtonStyle: SxProps<Theme> = {
  marginLeft: '10px',
  textTransform: 'none',
};

const iconButtonStyle: SxProps<Theme> = {
  padding: '8px',
  '&.MuiIconButton-root': {
    width: '36px',
    height: '36px',
  },
};

const loaderBoxStyle: SxProps<Theme> = {
  height: '36px',
  width: '36px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const animationStyle: React.CSSProperties = {
  height: '100%',
  width: '100%',
};
